import React, { useState, useEffect } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../services/firebase';
import { registrarActividad } from '../services/actividadService';
import { crearContrasenaPrimerIngreso } from '../services/bienvenidaService';
import PrimerIngreso from './PrimerIngreso';
import './Login.css';
import { KeyIcon, EnvelopeIcon, ArrowPathIcon, EyeIcon, EyeSlashIcon } from '../components/ui/Icons';

const AVISO_PRIMER_INGRESO = 'Antes de entrar tenés que crear tu propia contraseña: escribí tu correo y la contraseña que te dieron (la de tu carta de bienvenida o la que te pasó un administrador) y después vas a crear la tuya.';

function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  // Primer ingreso (programa de Bienvenida, CURSOS_PLAN.md §0.1 #46): quien entra con la contraseña de su carta tiene que
  // crear la suya antes de ver el sistema; la del papel deja de servir. La marca `debeCambiarPassword` la pone y la quita
  // SOLO el servidor (functions/bienvenida/). El formulario es PrimerIngreso.js.
  const [primerIngreso, setPrimerIngreso] = useState(false);
  const [motivoCambio, setMotivoCambio] = useState('carta'); // 'carta' o 'nueva_pass' (lo marca el servidor)

  // Si llegó acá desde una sesión que todavía no creó su contraseña (ProtectedRoute, App.js), esa sesión se cierra:
  // el cambio se hace siempre justo después de escribir la contraseña de la carta.
  useEffect(() => {
    if (location.state && location.state.primerIngresoPendiente) {
      setSuccess(AVISO_PRIMER_INGRESO);
      if (auth.currentUser) signOut(auth).catch((e) => console.error('Error cerrando la sesión del primer ingreso:', e));
      navigate('/login', { replace: true, state: null }); // que una recarga no lo vuelva a hacer
    }
  }, [location.state, navigate]);

  // Función para iniciar sesión
  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      // 1. Autenticar con Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;
      
      // 2. Consultar el documento del usuario en Firestore para verificar si está activo
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../services/firebase');
      
      const usuarioDoc = await getDoc(doc(db, 'usuarios', user.uid));
      
      if (!usuarioDoc.exists()) {
        // Si no existe el documento, cerrar sesión y mostrar error
        await auth.signOut();
        setError('Tu cuenta no está registrada en el sistema. Contacta al administrador.');
        setLoading(false);
        return;
      }
      
      const datosUsuario = usuarioDoc.data();
      
      // 3. Verificar si el usuario está activo
      if (datosUsuario.activo !== true) {
        // Si está inactivo, cerrar sesión y mostrar error
        await auth.signOut();
        setError('Tu cuenta está inactiva. Contacta al administrador para reactivarla.');
        setLoading(false);
        return;
      }

      // 4. Entró con una contraseña que le dieron (la de su carta de bienvenida o la de "Nueva Pass"): antes de entrar,
      //    crea la suya.
      if (datosUsuario.debeCambiarPassword === true) {
        setMotivoCambio(datosUsuario.cambioPasswordMotivo === 'nueva_pass' ? 'nueva_pass' : 'carta');
        setPrimerIngreso(true);
        setSuccess('');
        setLoading(false);
        return;
      }

      // 5. Si todo está bien, permitir el ingreso
      registrarActividad('sesion', 'login', 'Inició sesión'); // auditoría (best-effort)
      setTimeout(() => {
        navigate('/');
      }, 500);
      
    } catch (error) {
      if (error.code === 'auth/user-not-found') {
        setError('Usuario no encontrado.');
      } else if (error.code === 'auth/wrong-password') {
        setError('Contraseña incorrecta.');
      } else if (error.code === 'auth/invalid-email') {
        setError('Correo electrónico inválido.');
      } else if (error.code === 'auth/invalid-credential') {
        setError('Credenciales inválidas. Verifica tu correo y contraseña.');
      } else {
        setError('Error al iniciar sesión. Verifica tus credenciales.');
      }
      console.error(error);
      setLoading(false);
    }
  };

  // Primer ingreso: la persona crea su propia contraseña. El servidor la pone, cierra las sesiones abiertas con la de la
  // carta y quita la marca; acá se entra de nuevo con la contraseña nueva (así la sesión y la ficha cargan limpias).
  const salirDelPrimerIngreso = (mensajeError) => {
    signOut(auth).catch((e) => console.error('Error cerrando sesión:', e));
    setPrimerIngreso(false);
    setPassword('');
    setSuccess('');
    setError(mensajeError || '');
  };

  // `nueva` ya viene revisada por PrimerIngreso.js (la decisión final es del servidor).
  const handleCrearContrasena = async (nueva) => {
    setError('');
    setLoading(true);
    try {
      await crearContrasenaPrimerIngreso(nueva);
    } catch (err) {
      console.error('Error creando la contraseña del primer ingreso:', err);
      setLoading(false);
      if (err.sesionVieja) { salirDelPrimerIngreso(err.message); return; }
      if (err.yaTieneLaSuya) { salirDelPrimerIngreso('Tu cuenta ya tiene su propia contraseña: entrá con ella.'); return; }
      setError(err.message || 'No se pudo guardar tu contraseña. Probá de nuevo.');
      return;
    }
    try {
      await signOut(auth);
      const credencial = await signInWithEmailAndPassword(auth, email, nueva);
      // Igual que el ingreso normal: leer la ficha antes de entrar le da tiempo a AuthContext de cargarla (si no, con
      // mala señal ProtectedRoute podría ver la sesión todavía vacía y devolverla al Login sin decir nada).
      const { doc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../services/firebase');
      await getDoc(doc(db, 'usuarios', credencial.user.uid));
      setPassword('');
      registrarActividad('sesion', 'login', 'Inició sesión'); // auditoría (best-effort)
      setTimeout(() => {
        navigate('/');
      }, 500);
    } catch (err) {
      // La contraseña ya quedó guardada: solo falló volver a entrar.
      console.error('Error entrando con la contraseña nueva:', err);
      setLoading(false);
      salirDelPrimerIngreso('');
      setSuccess('Tu contraseña quedó guardada. Entrá con tu correo y tu contraseña nueva.');
    }
  };

  // Función para recuperar contraseña
  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Se ha enviado un correo para restablecer tu contraseña. Revisa tu bandeja de entrada y spam.');
      setTimeout(() => {
        setShowResetPassword(false);
        setSuccess('');
      }, 5000);
    } catch (error) {
      console.error('Error completo:', error);
      
      if (!error || error === null) {
        setError('Error desconocido al enviar el correo. Verifica tu conexión.');
      } else if (error.code === 'auth/user-not-found') {
        setError('No existe una cuenta con este correo.');
      } else if (error.code === 'auth/invalid-email') {
        setError('El formato del correo electrónico no es válido.');
      } else if (error.code === 'auth/missing-email') {
        setError('Debes ingresar un correo electrónico.');
      } else {
        setError(`Error al enviar el correo: ${error.message || 'Intenta de nuevo.'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      {/* Elementos del fondo animado (aumentado a 25) */}
      <div className="background-shapes">
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
        <span></span>
      </div>
      
      {/* Frase inspiradora */}
      <p className="inspirational-quote">
        "La innovación es lo que distingue a un líder de los demás."
      </p>

      <div className="login-box">
        <div className="login-header">
          <h1>Sistema Administrativo</h1>
          
          <div className="logo-container">
            <img 
              src="/logo.png" 
              alt="PARALELLUM" 
              className="logo"
              onError={(e) => {
                e.target.style.display = 'none';
                document.getElementById('logo-text').style.display = 'block';
              }}
            />
            <h2 id="logo-text" style={{display: 'none'}}>PARALELLUM</h2>
          </div>
        </div>

        {primerIngreso ? (
          // Primer ingreso: crear la contraseña propia (la de la carta deja de servir)
          <PrimerIngreso
            email={email}
            contrasenaCarta={password}
            motivo={motivoCambio}
            loading={loading}
            error={error}
            onGuardar={handleCrearContrasena}
            onVolver={() => salirDelPrimerIngreso('')}
          />
        ) : !showResetPassword ? (
          // Formulario de login normal
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Correo Electrónico:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="correo@ejemplo.com"
                disabled={loading}
              />
            </div>

            <div className="form-group">
              <label>Contraseña:</label>
              <div style={{ position: 'relative' }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  placeholder="••••••"
                  disabled={loading}
                  style={{ paddingRight: '44px' }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  title={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '10px',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    padding: '4px',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    color: '#888',
                    display: 'inline-flex',
                    alignItems: 'center'
                  }}
                >
                  {showPassword
                    ? <EyeSlashIcon className="w-5 h-5" />
                    : <EyeIcon className="w-5 h-5" />
                  }
                </button>
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading
                ? <><ArrowPathIcon className="w-4 h-4" /> Procesando...</>
                : <><KeyIcon className="w-4 h-4" /> Iniciar Sesión</>
              }
            </button>

            <div className="login-footer">
              <button 
                type="button"
                onClick={() => {
                  setShowResetPassword(true);
                  setError('');
                  setSuccess('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  padding: '5px'
                }}
              >
                ¿Olvidaste tu contraseña?
              </button>
            </div>
          </form>
        ) : (
          // Formulario de recuperación de contraseña
          <form onSubmit={handleResetPassword}>
            <div className="form-group">
              <label>Ingresa tu correo electrónico:</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="correo@ejemplo.com"
                disabled={loading}
              />
            </div>

            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <button
              type="submit"
              disabled={loading}
              style={{
                width: '100%',
                padding: '12px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: loading ? 'not-allowed' : 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'transform 0.2s',
                opacity: loading ? 0.7 : 1
              }}
            >
              {loading
                ? <><ArrowPathIcon className="w-4 h-4" /> Enviando...</>
                : <><EnvelopeIcon className="w-4 h-4" /> Enviar correo de recuperación</>
              }
            </button>

            <div className="login-footer">
              <button 
                type="button"
                onClick={() => {
                  setShowResetPassword(false);
                  setError('');
                  setSuccess('');
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#667eea',
                  cursor: 'pointer',
                  fontSize: '14px',
                  textDecoration: 'underline',
                  padding: '5px'
                }}
              >
                Volver al inicio de sesión
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

export default Login;